/* eslint-disable @typescript-eslint/naming-convention */
import { colors, HighlightIcon, Icon, Tooltip } from '@dev-spendesk/grapes';
import cx from 'classnames';
import React from 'react';

import { EllipsisTooltip } from 'common/components/EllipsisTooltip';
import { useTranslation } from 'common/hooks/useTranslation';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';

import {
  type BreakdownPart,
  getBudgetLimit,
  getSpentAmount,
} from '../../models/breakdown';
import {
  type ExpenseCategoryBreakdown,
  getExpenseCategoryName,
  getExpenseCategorySpentPercentage,
  isExpenseCategoryNotInBudget,
} from '../../models/expenseCategoryBreakdown';
import { BudgetGauge } from '../BudgetGauge';

type Props = {
  breakdown: ExpenseCategoryBreakdown;
  isExpenseCategoryArchived: boolean;
  onGaugeSegmentClick: (
    breakdownPart: 'committed' | 'used',
    breakdown: ExpenseCategoryBreakdown,
  ) => void;
};

export const BudgetExpenseCategoriesBreakdownRow = ({
  breakdown,
  isExpenseCategoryArchived,
  onGaugeSegmentClick,
}: Props) => {
  const { t } = useTranslation('global');

  const { expenseCategory } = breakdown;

  return (
    <div
      className="flex h-56 items-center justify-between gap-16"
      key={expenseCategory.id}
    >
      <div className="w-[25%] max-w-[231px] text-content-primary body-m">
        <div className="flex items-center gap-8">
          <EllipsisTooltip
            text={getExpenseCategoryName(expenseCategory, t)}
            placement="top"
          />
          {isExpenseCategoryArchived && (
            <Tooltip
              content={t('budget.overviewPage.expenseCategoryDeleted')}
              placement="top"
            >
              <Icon
                className="mt-[5px]"
                size="m"
                color={colors.contentDecorativeIcon}
                name="circle-information"
              />
            </Tooltip>
          )}
        </div>
      </div>
      <div
        className={cx('w-[30%] body-m', {
          'text-content-primary': isExpenseCategoryArchived,
        })}
      >
        {getExpenseCategoryAmountCell(breakdown)}
      </div>
      <div className="w-[35%]">
        {renderGaugeColumn(breakdown, onGaugeSegmentClick)}
      </div>
      <div
        className={cx('w-[10%] whitespace-nowrap body-m', {
          'text-content-primary': isExpenseCategoryArchived,
        })}
      >
        {!isExpenseCategoryArchived &&
          getExpenseCategoryPercentageCell(breakdown)}
      </div>
    </div>
  );
};

const renderGaugeColumn = (
  breakdown: ExpenseCategoryBreakdown,
  onGaugeSegmentClick: (
    selectedBreakdownPart: BreakdownPart,
    selectedBreakdown: ExpenseCategoryBreakdown,
  ) => void,
) => {
  const { expenseCategory } = breakdown;
  return (
    <BudgetGauge
      budgetBreakdown={breakdown}
      gaugeConfiguration={{
        tooltip: { mode: 'normal' },
        enforceWarningState: expenseCategory.isOther,
      }}
      onGaugeSegmentClick={(breakdownPart) =>
        onGaugeSegmentClick(breakdownPart, breakdown)
      }
    />
  );
};

const getExpenseCategoryPercentageCell = (
  breakdown: ExpenseCategoryBreakdown,
): React.ReactNode | string | null => {
  if (
    isExpenseCategoryNotInBudget(breakdown) ||
    breakdown.expenseCategory.isOther
  ) {
    return null;
  }

  const percentage = getExpenseCategorySpentPercentage(breakdown);
  if (percentage > 100) {
    return (
      <>
        <span className="text-content-warning-default title-m">{percentage}%</span>
        <HighlightIcon
          className="ml-8"
          name="triangle-warning"
          size={24}
          variant="warning"
        />
      </>
    );
  }
  return `${percentage}%`;
};

const getExpenseCategoryAmountCell = (
  breakdown: ExpenseCategoryBreakdown,
): React.ReactNode => {
  const amount = breakdown.expenseCategory.isOther
    ? { ...breakdown.available, amount: 0 }
    : getBudgetLimit(breakdown);

  return (
    <span>
      {formatMonetaryValue(getSpentAmount(breakdown))}/{' '}
      <span className="text-content-primary">{formatMonetaryValue(amount)}</span>
    </span>
  );
};
